import type React from 'react';

export type CompareSliderBoundsPartial = { left: number; width: number };
export type CompareSliderBoundsFull = CompareSliderBoundsPartial & { top: number; height: number };

function bindPointerDrag(
  el: HTMLElement,
  pointerId: number,
  setDragging: ((v: boolean) => void) | undefined,
  applyClientX: (clientX: number) => void,
): void {
  let captured = false;
  if (typeof el.setPointerCapture === 'function') {
    try {
      el.setPointerCapture(pointerId);
      captured = true;
    } catch {
      /* Safari 部分嵌套 transform/overflow 下捕获失败，改为 document 监听 */
    }
  }
  const root: EventTarget = captured ? el : document;
  setDragging?.(true);

  const onPointerMove = (ev: Event) => {
    const pe = ev as PointerEvent;
    if (pe.pointerId !== pointerId) return;
    pe.preventDefault();
    applyClientX(pe.clientX);
  };

  const onPointerEnd = (ev: Event) => {
    const pe = ev as PointerEvent;
    if (pe.pointerId !== pointerId) return;
    setDragging?.(false);
    if (captured) {
      try {
        el.releasePointerCapture(pointerId);
      } catch {
        /* ignore */
      }
    }
    root.removeEventListener('pointermove', onPointerMove);
    root.removeEventListener('pointerup', onPointerEnd);
    root.removeEventListener('pointercancel', onPointerEnd);
  };

  root.addEventListener('pointermove', onPointerMove, { passive: false });
  root.addEventListener('pointerup', onPointerEnd);
  root.addEventListener('pointercancel', onPointerEnd);
}

/** iPad Safari：pointer 链路易哑火，用 Touch + document touchmove（passive: false）兜底 */
function bindTouchDrag(
  setDragging: ((v: boolean) => void) | undefined,
  applyClientX: (clientX: number) => void,
): void {
  setDragging?.(true);
  const onMove = (ev: TouchEvent) => {
    if (ev.touches.length === 0) return;
    ev.preventDefault();
    applyClientX(ev.touches[0].clientX);
  };
  const onEnd = () => {
    setDragging?.(false);
    document.removeEventListener('touchmove', onMove);
    document.removeEventListener('touchend', onEnd);
    document.removeEventListener('touchcancel', onEnd);
  };
  document.addEventListener('touchmove', onMove, { passive: false });
  document.addEventListener('touchend', onEnd);
  document.addEventListener('touchcancel', onEnd);
}

/** 侧栏 / 弹窗：相对图片矩形计算百分比，并同步 imageBounds（相对容器 left/width） */
export function startCompareSliderPointerDrag(
  e: React.PointerEvent<HTMLElement>,
  opts: {
    originalImageRef: React.RefObject<HTMLImageElement | null>;
    imageContainerRef: React.RefObject<HTMLElement | null>;
    setSliderPercent: (pct: number) => void;
    setDragging: (v: boolean) => void;
    setImageBounds: (b: CompareSliderBoundsPartial) => void;
  },
): void {
  if (e.pointerType === 'mouse' && e.button !== 0) return;
  /* 手指触控交给 onTouchStart，避免与 Touch 序列重复且规避 Safari pointer 异常 */
  if (e.pointerType === 'touch') {
    e.preventDefault();
    e.stopPropagation();
    return;
  }
  e.preventDefault();
  e.stopPropagation();

  const apply = (clientX: number) => {
    const img = opts.originalImageRef.current;
    const container = opts.imageContainerRef.current;
    if (!img) return;
    const imageRect = img.getBoundingClientRect();
    if (imageRect.width <= 0) return;
    const mouseX = clientX - imageRect.left;
    const pct = Math.max(0, Math.min(100, (mouseX / imageRect.width) * 100));
    opts.setSliderPercent(pct);
    if (container) {
      const containerRect = container.getBoundingClientRect();
      opts.setImageBounds({
        left: imageRect.left - containerRect.left,
        width: imageRect.width,
      });
    }
  };

  apply(e.clientX);
  bindPointerDrag(e.currentTarget, e.pointerId, opts.setDragging, apply);
}

export function startCompareSliderTouchDrag(
  e: React.TouchEvent<HTMLElement>,
  opts: {
    originalImageRef: React.RefObject<HTMLImageElement | null>;
    imageContainerRef: React.RefObject<HTMLElement | null>;
    setSliderPercent: (pct: number) => void;
    setDragging: (v: boolean) => void;
    setImageBounds: (b: CompareSliderBoundsPartial) => void;
  },
): void {
  if (e.touches.length !== 1) return;
  e.preventDefault();
  e.stopPropagation();

  const apply = (clientX: number) => {
    const img = opts.originalImageRef.current;
    const container = opts.imageContainerRef.current;
    if (!img) return;
    const imageRect = img.getBoundingClientRect();
    if (imageRect.width <= 0) return;
    const mouseX = clientX - imageRect.left;
    const pct = Math.max(0, Math.min(100, (mouseX / imageRect.width) * 100));
    opts.setSliderPercent(pct);
    if (container) {
      const containerRect = container.getBoundingClientRect();
      opts.setImageBounds({
        left: imageRect.left - containerRect.left,
        width: imageRect.width,
      });
    }
  };

  apply(e.touches[0].clientX);
  bindTouchDrag(opts.setDragging, apply);
}

/** Editor 主界面对比：同步完整 bounds（含 top/height） */
export function startEditorCompareSliderPointerDrag(
  e: React.PointerEvent<HTMLElement>,
  opts: {
    originalImageRef: React.RefObject<HTMLImageElement | null>;
    containerRef: React.RefObject<HTMLElement | null>;
    setSliderPercent: (pct: number) => void;
    setDragging: (v: boolean) => void;
    setImageBounds: (b: CompareSliderBoundsFull) => void;
  },
): void {
  if (e.pointerType === 'mouse' && e.button !== 0) return;
  if (e.pointerType === 'touch') {
    e.preventDefault();
    e.stopPropagation();
    return;
  }
  e.preventDefault();
  e.stopPropagation();

  const apply = (clientX: number) => {
    const img = opts.originalImageRef.current;
    const container = opts.containerRef.current;
    if (!img || !container) return;
    const imageRect = img.getBoundingClientRect();
    if (imageRect.width <= 0) return;
    const mouseX = clientX - imageRect.left;
    let pct = (mouseX / imageRect.width) * 100;
    pct = Math.max(0, Math.min(100, pct));
    opts.setSliderPercent(pct);
    const containerRect = container.getBoundingClientRect();
    opts.setImageBounds({
      left: imageRect.left - containerRect.left,
      top: imageRect.top - containerRect.top,
      width: imageRect.width,
      height: imageRect.height,
    });
  };

  apply(e.clientX);
  bindPointerDrag(e.currentTarget, e.pointerId, opts.setDragging, apply);
}

export function startEditorCompareSliderTouchDrag(
  e: React.TouchEvent<HTMLElement>,
  opts: {
    originalImageRef: React.RefObject<HTMLImageElement | null>;
    containerRef: React.RefObject<HTMLElement | null>;
    setSliderPercent: (pct: number) => void;
    setDragging: (v: boolean) => void;
    setImageBounds: (b: CompareSliderBoundsFull) => void;
  },
): void {
  if (e.touches.length !== 1) return;
  e.preventDefault();
  e.stopPropagation();

  const apply = (clientX: number) => {
    const img = opts.originalImageRef.current;
    const container = opts.containerRef.current;
    if (!img || !container) return;
    const imageRect = img.getBoundingClientRect();
    if (imageRect.width <= 0) return;
    const mouseX = clientX - imageRect.left;
    let pct = (mouseX / imageRect.width) * 100;
    pct = Math.max(0, Math.min(100, pct));
    opts.setSliderPercent(pct);
    const containerRect = container.getBoundingClientRect();
    opts.setImageBounds({
      left: imageRect.left - containerRect.left,
      top: imageRect.top - containerRect.top,
      width: imageRect.width,
      height: imageRect.height,
    });
  };

  apply(e.touches[0].clientX);
  bindTouchDrag(opts.setDragging, apply);
}

/** 物理重构等：百分比相对 wrapper 矩形（与 clipPath 同一参照） */
export function startCompareSliderPointerDragFromWrapper(
  e: React.PointerEvent<HTMLElement>,
  opts: {
    wrapperRef: React.RefObject<HTMLElement | null>;
    setSliderPercent: (pct: number) => void;
    setDragging?: (v: boolean) => void;
  },
): void {
  if (e.pointerType === 'mouse' && e.button !== 0) return;
  if (e.pointerType === 'touch') {
    e.preventDefault();
    e.stopPropagation();
    return;
  }
  e.preventDefault();
  e.stopPropagation();

  const apply = (clientX: number) => {
    const wrap = opts.wrapperRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    if (rect.width <= 0) return;
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    opts.setSliderPercent(pct);
  };

  apply(e.clientX);
  bindPointerDrag(e.currentTarget, e.pointerId, opts.setDragging, apply);
}

export function startCompareSliderTouchDragFromWrapper(
  e: React.TouchEvent<HTMLElement>,
  opts: {
    wrapperRef: React.RefObject<HTMLElement | null>;
    setSliderPercent: (pct: number) => void;
    setDragging?: (v: boolean) => void;
  },
): void {
  if (e.touches.length !== 1) return;
  e.preventDefault();
  e.stopPropagation();

  const apply = (clientX: number) => {
    const wrap = opts.wrapperRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    if (rect.width <= 0) return;
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    opts.setSliderPercent(pct);
  };

  apply(e.touches[0].clientX);
  bindTouchDrag(opts.setDragging, apply);
}
